import { useState, useEffect, useMemo } from 'react';
import {
  Loader2,
  Euro,
  CalendarRange,
  Moon,
  Users,
  TrendingUp,
  Percent,
  Building2,
  Filter,
  X,
  Tag,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { api } from '@/lib/api';
import type { AnalyticsFiltersData, AnalyticsResult, CompareResult, FacturacionSerenoResult, ModoAlicante } from '@/lib/types';
import { ZONE_LABELS, classNames } from '@/lib/utils';
import { yearRange } from '@/lib/analyticsDates';
import { KPI_INFO } from '@/lib/kpiInfo';
import MultiSelect from '@/components/ui/MultiSelect';
import InfoTooltip from '@/components/ui/InfoTooltip';
import FiltroAlicante from '@/components/analytics/FiltroAlicante';
import YearComparison from '@/components/analytics/YearComparison';
import FacturacionSereno from '@/components/analytics/FacturacionSereno';
import PanelFiltros, { SelectorPeriodo } from '@/components/analytics/PanelFiltros';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const CHART_COLORS_LIGHT = ['#0B1D2E', '#3B6EA5', '#6FA8DC', '#A3C2E0', '#D1E0F0', '#8FB4D9', '#4A7BB0', '#243B53'];
const CHART_COLORS_DARK = ['#D1E0F0', '#6FA8DC', '#3B6EA5', '#8FB4D9', '#A3C2E0', '#4A7BB0', '#5B8AC0', '#2E5076'];

// Currency without decimals (Analytics shows whole euros only)
function fmtMoney(value: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTH_SHORT[Number(m) - 1] || m} ${String(y).slice(2)}`;
}

export default function AnalyticsPage() {
  const isDark = useIsDarkMode();
  const chart = {
    grid: isDark ? '#ffffff14' : '#0B1D2E15',
    axis: isDark ? '#9ca3af' : '#0B1D2E99',
    bar: isDark ? '#D1E0F0' : '#0B1D2E',
    cursor: isDark ? '#ffffff10' : '#0B1D2E08',
    palette: isDark ? CHART_COLORS_DARK : CHART_COLORS_LIGHT,
  };
  const [filtersData, setFiltersData] = useState<AnalyticsFiltersData | null>(null);
  const [data, setData] = useState<AnalyticsResult | null>(null);
  const [loading, setLoading] = useState(false);
  // Lo que factura Sereno (la cifra de Holded), con los filtros con que se pidió
  const [facturacion, setFacturacion] = useState<FacturacionSerenoResult | null>(null);
  const [facturacionLoading, setFacturacionLoading] = useState(false);
  const [aplicados, setAplicados] = useState<FiltrosAplicados | null>(null);
  // Comparativa anual (año natural completo vs el anterior), independiente del periodo
  const [yoy, setYoy] = useState<CompareResult | null>(null);
  const [yoyLoading, setYoyLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const initial = yearRange(new Date().getFullYear());
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [zones, setZones] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [includeOwner, setIncludeOwner] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  // Propiedades familiares (Malia Bernal + CasaML): incluidas por defecto
  const [includeFamily, setIncludeFamily] = useState(true);
  // Alicante fuera por defecto: es otra operativa y mezclarla distorsiona las
  // medias. Como «solo activas», cuenta como filtro al SALIRSE de ese estado.
  const [alicante, setAlicante] = useState<ModoAlicante>('sin');
  const [prorate, setProrate] = useState(true);
  const [zoneCleaningView, setZoneCleaningView] = useState(false);
  const [monthCleaningView, setMonthCleaningView] = useState(false);
  const [propSort, setPropSort] = useState<{ key: 'property' | 'reservas' | 'noches' | 'facturacion' | 'limpieza'; dir: 'asc' | 'desc' }>({ key: 'facturacion', dir: 'desc' });

  useEffect(() => {
    api.getAnalyticsFilters().then(setFiltersData).catch(() => setFiltersData(null));
  }, []);

  const loadData = async (overrides?: Overrides) => {
    const params = {
      from: overrides?.from ?? from,
      to: overrides?.to ?? to,
      propertyIds: overrides?.propertyIds ?? propertyIds,
      zones: overrides?.zones ?? zones,
      channels: overrides?.channels ?? channels,
      includeOwner,
      activeOnly: overrides?.activeOnly ?? activeOnly,
      excludeFamily: !(overrides?.includeFamily ?? includeFamily),
      alicante: overrides?.alicante ?? alicante,
      prorate,
    };
    setLoading(true);
    setFacturacionLoading(true);
    setAplicados(params);
    api
      .getFacturacionSereno(params)
      .then(setFacturacion)
      .catch((e) => {
        console.error(e);
        setFacturacion(null);
      })
      .finally(() => setFacturacionLoading(false));
    try {
      const result = await api.getAnalytics(params);
      setData(result);
    } catch (e) {
      console.error(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Comparativa anual: siempre año natural completo actual vs anterior, con los
  // mismos filtros que la página (el periodo seleccionado no le afecta).
  const loadYoY = async (overrides?: Overrides) => {
    const cur = yearRange(currentYear);
    const prev = yearRange(currentYear - 1);
    setYoyLoading(true);
    try {
      const result = await api.compareAnalytics({
        fromA: cur.from,
        toA: cur.to,
        labelA: String(currentYear),
        fromB: prev.from,
        toB: prev.to,
        labelB: String(currentYear - 1),
        propertyIds: overrides?.propertyIds ?? propertyIds,
        zones: overrides?.zones ?? zones,
        channels: overrides?.channels ?? channels,
        includeOwner,
        activeOnly: overrides?.activeOnly ?? activeOnly,
        excludeFamily: !(overrides?.includeFamily ?? includeFamily),
        alicante: overrides?.alicante ?? alicante,
        prorate,
      });
      setYoy(result);
    } catch (e) {
      console.error(e);
      setYoy(null);
    } finally {
      setYoyLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadYoY();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When properties available, build options for selects
  const propertyOptions = useMemo(() => {
    const list = filtersData?.properties || [];
    let visible = activeOnly ? list.filter((p) => p.active) : list;
    if (!includeFamily) visible = visible.filter((p) => !p.family);
    if (alicante === 'sin') visible = visible.filter((p) => !p.alicante);
    if (alicante === 'solo') visible = visible.filter((p) => p.alicante);
    return visible.map((p) => ({ value: p.id, label: p.name }));
  }, [filtersData, activeOnly, includeFamily, alicante]);
  const zoneOptions = useMemo(
    () => (filtersData?.zones || []).map((z) => ({ value: z, label: ZONE_LABELS[z] || z })),
    [filtersData]
  );
  const channelOptions = useMemo(
    () => (filtersData?.channels || []).map((c) => ({ value: c, label: c })),
    [filtersData]
  );

  const monthChart = useMemo(
    () => (data?.byMonth || []).map((m) => ({
      name: monthLabel(m.month),
      Volumen: Math.round(m.facturacion),
      Limpieza: Math.round(m.limpieza),
      reservas: m.reservas,
    })),
    [data]
  );
  const channelChart = useMemo(
    () => (data?.byChannel || []).map((c) => ({ name: c.channel, value: Math.round(c.facturacion) })),
    [data]
  );

  const sortedProperties = useMemo(() => {
    const list = [...(data?.byProperty || [])];
    const { key, dir } = propSort;
    const mult = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (key === 'property') return mult * a.property.localeCompare(b.property);
      return mult * ((a[key] as number) - (b[key] as number));
    });
    return list;
  }, [data, propSort]);

  const toggleSort = (key: typeof propSort.key) => {
    setPropSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'property' ? 'asc' : 'desc' }
    );
  };

  // Los filtros con que se calculó la Facturación Sereno, en texto, para
  // enseñarlos al lado de la cifra: Holded no tiene ninguno.
  const filtrosFacturacion = useMemo(() => {
    if (!aplicados) return [];
    const out: string[] = [];
    if (aplicados.activeOnly) out.push('Solo activas');
    if (aplicados.alicante === 'sin') out.push('Sin Alicante');
    if (aplicados.alicante === 'solo') out.push('Solo Alicante');
    if (aplicados.excludeFamily) out.push('Sin familiares');
    if (aplicados.zones.length) out.push(`Zonas: ${aplicados.zones.map((z) => ZONE_LABELS[z] || z).join(', ')}`);
    if (aplicados.channels.length) out.push(`Canales: ${aplicados.channels.join(', ')}`);
    if (aplicados.propertyIds.length) {
      out.push(aplicados.propertyIds.length === 1 ? '1 propiedad' : `${aplicados.propertyIds.length} propiedades`);
    }
    return out;
  }, [aplicados]);

  const verComoHolded = () => {
    const sinFiltros = { activeOnly: false, includeFamily: true, alicante: 'todas' as ModoAlicante, zones: [], channels: [], propertyIds: [] };
    setActiveOnly(false);
    setIncludeFamily(true);
    setAlicante('todas');
    setZones([]);
    setChannels([]);
    setPropertyIds([]);
    loadData(sinFiltros);
    loadYoY(sinFiltros);
  };

  // «Solo activas» e «incluir familiares» son el estado por defecto: cuentan al
  // APAGARSE. Imputar por entrada también suma: cambia los datos y con el panel
  // plegado nada más lo delataría.
  const activeFilterCount =
    propertyIds.length +
    zones.length +
    channels.length +
    (includeOwner ? 1 : 0) +
    (activeOnly ? 0 : 1) +
    (includeFamily ? 0 : 1) +
    (alicante === 'sin' ? 0 : 1) +
    (prorate ? 0 : 1);

  return (
    <div>
      <PanelFiltros
        activos={activeFilterCount}
        barra={
          <SelectorPeriodo
            from={from}
            to={to}
            onChange={(r) => {
              setFrom(r.from);
              setTo(r.to);
              loadData({ from: r.from, to: r.to });
            }}
          />
        }
      >
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input min-h-11" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input min-h-11" />
          </div>
          <MultiSelect label="Zonas" icon={Filter} options={zoneOptions} selected={zones} onChange={setZones} allLabel="Todas" />
          <MultiSelect label="Canales" icon={Tag} options={channelOptions} selected={channels} onChange={setChannels} allLabel="Todos" searchable />
          <MultiSelect label="Propiedades" icon={Building2} options={propertyOptions} selected={propertyIds} onChange={setPropertyIds} allLabel="Todas" searchable />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input type="checkbox" checked={includeOwner} onChange={(e) => setIncludeOwner(e.target.checked)} className="rounded" />
            Incluir estancias Owner
          </label>

          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                const next = e.target.checked;
                setActiveOnly(next);
                if (next) setPropertyIds((ids) => ids.filter((id) => filtersData?.properties.find((p) => p.id === id)?.active));
              }}
              className="rounded"
            />
            Solo propiedades activas (PMS)
          </label>

          <label
            className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer"
            title="Malia Bernal (3) y CasaML (2). Al desactivarlas salen de todos los cálculos."
          >
            <input
              type="checkbox"
              checked={includeFamily}
              onChange={(e) => {
                const next = e.target.checked;
                setIncludeFamily(next);
                // Al desactivarlas, quítalas también de la selección de propiedades
                const nextIds = next
                  ? propertyIds
                  : propertyIds.filter((id) => !filtersData?.properties.find((p) => p.id === id)?.family);
                setPropertyIds(nextIds);
                loadData({ includeFamily: next, propertyIds: nextIds });
                loadYoY({ includeFamily: next, propertyIds: nextIds });
              }}
              className="rounded"
            />
            Incluir propiedades familiares
          </label>

          <FiltroAlicante
            valor={alicante}
            onChange={(next) => {
              setAlicante(next);
              // Las propiedades ya elegidas que dejan de contar salen también
              // de la selección, para no filtrar por algo que no se ve.
              const nextIds = propertyIds.filter((id) => {
                const p = filtersData?.properties.find((x) => x.id === id);
                if (!p) return true;
                return next === 'sin' ? !p.alicante : next === 'solo' ? p.alicante : true;
              });
              setPropertyIds(nextIds);
              loadData({ alicante: next, propertyIds: nextIds });
              loadYoY({ alicante: next, propertyIds: nextIds });
            }}
          />

          {/* Imputación: por noche (prorrateo, estilo Hostify) vs por entrada */}
          <div className="min-h-11 flex items-center gap-2">
            <span className="text-sm text-sereno-dark/60">Imputar por:</span>
            <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden">
              <button
                onClick={() => setProrate(true)}
                className={classNames(
                  'min-h-11 px-3 text-xs font-medium transition-colors',
                  prorate ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
                )}
              >
                Noche
              </button>
              <button
                onClick={() => setProrate(false)}
                className={classNames(
                  'min-h-11 px-3 text-xs font-medium transition-colors',
                  !prorate ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20'
                )}
              >
                Entrada
              </button>
            </div>
            <InfoTooltip text="Noche: el importe de cada reserva se reparte proporcionalmente entre las noches que caen dentro del periodo (devengo, igual que Hostify). Entrada: el importe completo se asigna al mes de check-in." />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button onClick={() => { loadData(); loadYoY(); }} disabled={loading} className="btn-primary min-h-11 flex items-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <TrendingUp size={16} />}
            Calcular
          </button>

          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setPropertyIds([]);
                setZones([]);
                setChannels([]);
                setIncludeOwner(false);
                setActiveOnly(true);
                setIncludeFamily(true);
                setAlicante('sin');
                setProrate(true);
              }}
              className="min-h-11 flex items-center gap-1.5 text-sm text-sereno-dark/50 hover:text-sereno-dark"
            >
              <X size={14} /> Limpiar filtros ({activeFilterCount})
            </button>
          )}
        </div>
      </PanelFiltros>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-sereno-dark/40" />
        </div>
      ) : !data ? (
        <div className="card text-center py-12 text-sereno-dark/50">No hay datos para el periodo seleccionado.</div>
      ) : (
        <>
          <FacturacionSereno
            data={facturacion}
            loading={facturacionLoading}
            filtros={filtrosFacturacion}
            onVerComoHolded={verComoHolded}
          />

          {/* KPIs: lo que pagan los huéspedes de todos los pisos (volumen) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <KPI k="facturacion" icon={Euro} value={fmtMoney(data.kpis.facturacion)} color="bg-emerald-50 text-emerald-600" />
            <KPI k="neto" icon={Euro} value={fmtMoney(data.kpis.neto)} color="bg-teal-50 text-teal-600" />
            <KPI k="reservas" icon={CalendarRange} value={String(data.kpis.reservas)} color="bg-blue-50 text-blue-600" />
            <KPI k="noches" icon={Moon} value={String(data.kpis.noches)} color="bg-indigo-50 text-indigo-600" />
            <KPI k="huespedes" icon={Users} value={String(data.kpis.huespedes)} color="bg-purple-50 text-purple-600" />
            <KPI k="adr" icon={TrendingUp} value={fmtMoney(data.kpis.adr)} color="bg-amber-50 text-amber-600" />
            <KPI k="estanciaMedia" icon={Moon} value={`${Math.round(data.kpis.estanciaMedia)} noches`} color="bg-sky-50 text-sky-600" />
            <KPI k="ocupacion" icon={Percent} value={`${Math.round(data.kpis.ocupacion)}%`} color="bg-rose-50 text-rose-600" />
          </div>

          {/* Desglose facturación */}
          <div className="card mb-6">
            <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-4">Desglose del volumen de reservas</h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Stat label="Alojamiento" value={data.desglose.alojamiento} />
              <Stat label="Limpieza" value={data.desglose.limpieza} />
              <Stat label="Extras" value={data.desglose.extras} />
              <Stat label="IVA de Booking (aparte en Hostify)" value={data.desglose.impuestos} />
              <Stat label="Comisión canal" value={data.desglose.comision} />
              <Stat label="Ingresos netos (Hostify)" value={data.desglose.neto} />
              {data.desglose.reembolsos > 0 && <Stat label="Reembolsos" value={data.desglose.reembolsos} negative />}
            </div>
          </div>

          {/* Monthly chart */}
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
                {monthCleaningView ? 'Limpieza por mes' : 'Volumen de reservas por mes'}
              </h2>
              <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden">
                <button
                  onClick={() => setMonthCleaningView(false)}
                  className={classNames('px-2.5 py-1 text-xs font-medium transition-colors', !monthCleaningView ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20')}
                >
                  Total
                </button>
                <button
                  onClick={() => setMonthCleaningView(true)}
                  className={classNames('px-2.5 py-1 text-xs font-medium transition-colors', monthCleaningView ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20')}
                >
                  Limpieza
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthChart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: chart.axis }} />
                <YAxis tick={{ fontSize: 12, fill: chart.axis }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: any) => fmtMoney(Number(v))} cursor={{ fill: chart.cursor }} />
                <Bar dataKey={monthCleaningView ? 'Limpieza' : 'Volumen'} fill={chart.bar} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Comparativa anual: año natural completo vs año anterior */}
          <YearComparison data={yoy} loading={yoyLoading} year={currentYear} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* By channel pie */}
            <div className="card">
              <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-4">Volumen de reservas por canal</h2>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={channelChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={false}>
                    {channelChart.map((_, i) => (
                      <Cell key={i} fill={chart.palette[i % chart.palette.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => fmtMoney(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* By zone table */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">
                  {zoneCleaningView ? 'Limpieza por zona' : 'Volumen de reservas por zona'}
                </h2>
                <div className="inline-flex rounded-lg border border-sereno-dark/15 overflow-hidden">
                  <button
                    onClick={() => setZoneCleaningView(false)}
                    className={classNames('px-2.5 py-1 text-xs font-medium transition-colors', !zoneCleaningView ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20')}
                  >
                    Total
                  </button>
                  <button
                    onClick={() => setZoneCleaningView(true)}
                    className={classNames('px-2.5 py-1 text-xs font-medium transition-colors', zoneCleaningView ? 'bg-sereno-dark text-white an-active' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/20')}
                  >
                    Limpieza
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {[...data.byZone]
                  .sort((a, b) => (zoneCleaningView ? b.limpieza - a.limpieza : b.facturacion - a.facturacion))
                  .map((z) => (
                    <div key={z.zone} className="flex items-center justify-between py-1.5 border-b border-sereno-dark/5 last:border-0">
                      <span className="text-sm">{ZONE_LABELS[z.zone] || z.zone}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-sereno-dark/40">{z.reservas} res.</span>
                        <span className="text-sm font-semibold w-28 text-right">
                          {fmtMoney(zoneCleaningView ? z.limpieza : z.facturacion)}
                        </span>
                      </div>
                    </div>
                  ))}
                {data.byZone.length === 0 && <p className="text-sm text-sereno-dark/40">Sin datos</p>}
              </div>
            </div>
          </div>

          {/* By property table */}
          <div className="card">
            <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-4">Volumen de reservas por propiedad</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
                    <SortHeader label="Propiedad" sortKey="property" sort={propSort} onSort={toggleSort} />
                    <SortHeader label="Reservas" sortKey="reservas" sort={propSort} onSort={toggleSort} align="right" />
                    <SortHeader label="Noches" sortKey="noches" sort={propSort} onSort={toggleSort} align="right" />
                    <SortHeader label="Volumen" sortKey="facturacion" sort={propSort} onSort={toggleSort} align="right" />
                    <SortHeader label="Limpieza" sortKey="limpieza" sort={propSort} onSort={toggleSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedProperties.map((p) => (
                    <tr key={p.property} className="border-b border-sereno-dark/5 last:border-0">
                      <td className="py-2">
                        <span className="inline-flex items-center gap-1.5">
                          {p.property}
                          {p.listingId && (
                            <a
                              href={`https://app.hostify.com/listings/view/${p.listingId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Ver listing ${p.listingId} en Hostify`}
                              className="text-sereno-dark/30 hover:text-sereno-dark transition-colors"
                            >
                              <ExternalLink size={13} />
                            </a>
                          )}
                        </span>
                      </td>
                      <td className="py-2 text-right text-sereno-dark/60">{p.reservas}</td>
                      <td className="py-2 text-right text-sereno-dark/60">{p.noches}</td>
                      <td className="py-2 text-right font-semibold">{fmtMoney(p.facturacion)}</td>
                      <td className="py-2 text-right font-semibold">{fmtMoney(p.limpieza)}</td>
                    </tr>
                  ))}
                  {data.byProperty.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-4 text-center text-sereno-dark/40">Sin datos</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface FiltrosAplicados {
  from: string;
  to: string;
  propertyIds: string[];
  zones: string[];
  channels: string[];
  includeOwner: boolean;
  activeOnly: boolean;
  excludeFamily: boolean;
  alicante: ModoAlicante;
  prorate: boolean;
}

type Overrides = Partial<Pick<FiltrosAplicados, 'from' | 'to' | 'propertyIds' | 'zones' | 'channels' | 'activeOnly' | 'alicante'>> & {
  includeFamily?: boolean;
};

type PropSort = { key: 'property' | 'reservas' | 'noches' | 'facturacion' | 'limpieza'; dir: 'asc' | 'desc' };

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: PropSort['key'];
  sort: PropSort;
  onSort: (k: PropSort['key']) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={classNames('py-2 font-medium', align === 'right' ? 'text-right' : 'text-left')}>
      <button
        onClick={() => onSort(sortKey)}
        className={classNames(
          'inline-flex items-center gap-1 hover:text-sereno-dark transition-colors',
          align === 'right' && 'flex-row-reverse',
          active ? 'text-sereno-dark' : 'text-sereno-dark/50'
        )}
      >
        <span>{label}</span>
        <Icon size={12} className={active ? 'text-sereno-dark' : 'text-sereno-dark/30'} />
      </button>
    </th>
  );
}

function KPI({ k, icon: Icon, value, color }: { k: string; icon: React.ElementType; value: string; color: string }) {
  const info = KPI_INFO[k];
  return (
    <div className="card">
      <div className="flex items-start justify-between mb-2">
        <div className={classNames('p-2.5 rounded-lg', color)}>
          <Icon size={20} />
        </div>
        {info && <InfoTooltip text={info.info} />}
      </div>
      <p className="text-xs text-sereno-dark/50 truncate">{info?.label || k}</p>
      <p className="text-lg font-bold truncate">{value}</p>
    </div>
  );
}

function Stat({ label, value, negative }: { label: string; value: number; negative?: boolean }) {
  return (
    <div>
      <p className="text-xs text-sereno-dark/50 mb-1">{label}</p>
      <p className={classNames('text-lg font-bold', negative ? 'text-rose-600' : 'text-sereno-dark')}>
        {negative ? '-' : ''}
        {fmtMoney(value)}
      </p>
    </div>
  );
}
